import React, { useContext } from "react"
import type { ReactNode, CSSProperties } from "react"
import cx from "classnames"
import { DialogOverlay, DialogContent } from "@reach/dialog"
import LoadingAnimation from "../loading-animation"
import { Button } from "../button"
import { Icon } from "../icon"
import { VisuallyHidden } from "../visually-hidden"
import { PortalNode } from "../portal"
import { motion, AnimatePresence } from "framer-motion"
import ConfirmModal from "./confirm-modal"
import confirm from "./confirm"
import Action, { ActionProvider, Actions, ActionContext } from "./action"
import TitleBar from "./title-bar"
import { useMediaBreakpoint } from "../use-media-breakpoint"
import { useModalTelemetry } from "./use-modal-telemetry"
import type { TelemetryValue } from "../event-emitter"

const AnimatedDialogContent = motion.create(DialogContent)

const DEFAULT = "p-0 my-auto bg-white font-sans shadow max-h-screen"
const HEADER_DEFAULT = "flex items-center justify-between px-6"

const SIZE_CLASSES: Record<string, string> = {
  md: "max-w-2xl w-4/5",
  lg: "max-w-4xl w-4/5",
  full: "max-w-full w-4/5",
  "new-full": "max-w-full flex flex-col",
  fullscreen: "w-screen h-screen"
}

const SIZE_STYLES: Record<
  string,
  CSSProperties | ((md: boolean) => CSSProperties) | undefined
> = {
  md: {},
  lg: {},
  full: {
    maxWidth: "1600px",
    width: "calc(100vw - 4rem)",
    margin: "4rem auto 4rem auto"
  },
  "new-full": (md: boolean) => ({
    boxSizing: "border-box",
    width: "auto",
    margin: md ? "2.5rem" : "0.5rem",
    height: md ? "calc(100dvh - 5rem)" : "calc(100dvh - 1rem)", // margin top + margin bottom from the margin above
    overflow: "hidden"
  }),
  fullscreen: {
    margin: 0
  }
}

const MOBILE_FULLSCREEN_STYLES: CSSProperties = {
  margin: 0,
  width: "100vw",
  maxWidth: "none",
  height: "100dvh",
  maxHeight: "100dvh"
}

// The overlay adds 2rem of top padding, so this leaves a gutter on both sides.
const CONSTRAINED_MAX_HEIGHT = "calc(100dvh - 4rem)"

function getStyles(size: string, md: boolean): CSSProperties {
  const sizeStyle = SIZE_STYLES[size]
  if (typeof sizeStyle === "function") {
    return sizeStyle(md)
  }
  return sizeStyle ?? {}
}

type HeaderProps = {
  children: ReactNode
  className?: string
  separator?: boolean
}

function Header({ children, className, separator }: HeaderProps) {
  const { actions } = useContext(ActionContext)

  const classes = cx(HEADER_DEFAULT, className, {
    "border-b border-gray-200 py-3 md:py-5": actions.length > 0 || separator,
    "pt-6 pb-2": actions.length === 0 && !separator
  })

  return <div className={classes}>{children}</div>
}

export type ModalProps = {
  /** Content rendered inside the modal body. */
  children?: ReactNode
  /** Typically a string, but you can use JSX for more customizability. */
  label?: ReactNode
  /** A label that describes the modal on screen readers. Add this if you don't provide a label, or if you provide a JSX label. */
  ariaLabel?: string
  /** Kebab-case aria-label passed directly to the dialog overlay */
  "aria-label"?: string
  /** Called when the modal requests to be closed (close button or Escape key). */
  onClose: () => void
  /** Additional CSS classes applied to the dialog content element. */
  className?: string
  /** Controls the predefined width of the modal. You don't need to provide this if you set a `width`. */
  size?: "md" | "lg" | "full" | "fullscreen" | "custom" | "new-full"
  /** When true, shows a loading spinner instead of children. */
  loading?: boolean
  /** Explicit pixel or CSS width. Overrides the `size` max-width. */
  width?: string | number
  /** When true, hides the close button in the header. */
  hideCloseButton?: boolean
  /** Hides the header */
  hideHeader?: boolean
  /** Forces the separator under the title even when there are no Actions */
  separator?: boolean
  /** Data to emit alongside the "modal_open" and "modal_close" events. Set to `false` to disable emission. */
  telemetry?: TelemetryValue
  /**
   * When true, removes padding and rounded corners for full-bleed/edge-to-edge content.
   */
  fullBleed?: boolean
  /**
   * Expand the modal to fill the viewport below `md`
   */
  fullscreenOnMobile?: boolean
  /**
   * Cap the modal to the viewport height and scroll the body instead of letting
   * tall content overflow the dialog. Children can use `h-full` to fill the
   * available height.
   */
  constrainHeight?: boolean
  /** Override the overlay dismiss handler. Pass `undefined` to disable backdrop-click dismissal. */
  onDismiss?: () => void
  /** Temporary workaround to adjust z-index stacking for nested overlay scenarios. */
  "data-temp-z-index-fix"?: boolean
  /** Test identifier for automated testing. */
  "data-testid"?: string
  /** HTML id attribute for the dialog overlay element. */
  id?: string
  /** Inline styles applied to the dialog overlay. */
  style?: CSSProperties
}

/**
 * A dialog overlay for focused interactions such as forms, confirmations, and detail views.
 * Supports multiple size presets, an optional action bar (via the `Action` sub-component),
 * and animated entrance/exit via Framer Motion.
 */
export default function Modal(props: ModalProps) {
  const {
    children,
    label,
    ariaLabel,
    onClose,
    className,
    size = "md",
    loading = false,
    width,
    hideCloseButton = false,
    hideHeader = false,
    separator,
    telemetry,
    fullBleed = false,
    fullscreenOnMobile = false,
    constrainHeight = false,
    "aria-label": ariaLabelProp,
    onDismiss,
    "data-temp-z-index-fix": dataTempZIndexFix,
    "data-testid": dataTestId,
    id,
    style
  } = props

  const md = useMediaBreakpoint("md")
  const mobileFullscreen = fullscreenOnMobile && !md
  const boundedHeight = mobileFullscreen || constrainHeight
  const styles = mobileFullscreen
    ? MOBILE_FULLSCREEN_STYLES
    : {
        ...getStyles(size, md),
        maxWidth: width,
        ...(constrainHeight && { maxHeight: CONSTRAINED_MAX_HEIGHT })
      }

  useModalTelemetry({ label, ariaLabel, telemetry })

  // Consumers can pass onDismiss={undefined} to disable backdrop-click dismissal.
  // We use "in" to distinguish that from the prop not being passed at all.
  const overlayOnDismiss = "onDismiss" in props ? onDismiss : onClose

  return (
    <ActionProvider>
      <DialogOverlay
        onDismiss={overlayOnDismiss}
        className={
          mobileFullscreen || ["full", "fullscreen", "new-full"].includes(size)
            ? ""
            : "flex justify-center items-center pt-8"
        }
        aria-label={ariaLabelProp}
        data-temp-z-index-fix={dataTempZIndexFix}
        data-testid={dataTestId}
        id={id}
        style={style}
      >
        <AnimatePresence>
          <AnimatedDialogContent
            key="modal-content"
            aria-label={(ariaLabel || label) as string | undefined}
            className={cx(
              className,
              DEFAULT,
              !mobileFullscreen && SIZE_CLASSES[size],
              boundedHeight && "flex flex-col",
              !fullBleed && !mobileFullscreen && "rounded-lg"
            )}
            style={styles}
            initial={{
              opacity: 0,
              y: size === "full" || size === "new-full" ? 15 : -15
            }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {!hideHeader && (
              <Header
                className={cx("gap-8", boundedHeight && "shrink-0")}
                separator={separator}
              >
                {label ? <TitleBar content={label} /> : <div />}
                {!hideCloseButton && (
                  <Button
                    variant="tertiary"
                    onClick={onClose}
                    telemetry={false}
                  >
                    <Icon name="close" />
                    <VisuallyHidden>Close</VisuallyHidden>
                  </Button>
                )}
              </Header>
            )}
            {loading ? (
              <div className="flex justify-center">
                <LoadingAnimation />
              </div>
            ) : (
              <div
                className={cx("bg-white", {
                  "h-full": !boundedHeight,
                  "flex flex-col flex-1 min-h-0": boundedHeight,
                  "overflow-hidden": size === "new-full",
                  "rounded-b-lg": !fullBleed && !mobileFullscreen,
                  "rounded-t-lg": !fullBleed && hideHeader && !mobileFullscreen
                })}
              >
                <PortalNode
                  className={cx("bg-white", {
                    "h-full": !boundedHeight,
                    "flex-1 min-h-0 overflow-y-auto": boundedHeight,
                    "p-3 md:p-6": !fullBleed,
                    "rounded-b-lg": !fullBleed && !mobileFullscreen,
                    "rounded-t-lg":
                      !fullBleed && hideHeader && !mobileFullscreen
                  })}
                >
                  {children}
                </PortalNode>
                <Actions />
              </div>
            )}
          </AnimatedDialogContent>
        </AnimatePresence>
      </DialogOverlay>
    </ActionProvider>
  )
}

export { Modal }
export { Action, ConfirmModal, confirm }
